import store from '../store.js'
import api from '../api.js'
import { busy, notBusy } from '../busy/actions'
import { handleGetErrors, handleUpdateErrors } from '../error/actions'
import { resetSelectedItem, setSelectedItem } from '../selected/actions'
import redux from '../'
import {
  getEmailAddressComposeMode,
  getEmailSubjectComposeMode,
  isOutlookComposeMode,
  isOutlookReadMode,
  getEmailMetadataReadMode
} from '../../lib/office'
import { estimateTotalWithLookAhead, getPrefetchPagesAhead } from '../pagination/shared.js'
export const PROJECT_UPDATE_ACTION = 'PROJECT_UPDATE_ACTION'
export const PROJECT_DELETE_ACTION = 'PROJECT_DELETE_ACTION'

export const PROJECT_GET_PAGE_ITEMS_ACTION = 'PROJECT_GET_PAGE_ITEMS_ACTION'
export const PROJECT_GET_PAGE_ITEM_ACTION = 'PROJECT_GET_PAGE_ITEM_ACTION'
export const PROJECT_GET_PERSON_BY_EMAIL_ACTION = 'PROJECT_GET_PERSON_BY_EMAIL_ACTION'
export const PROJECT_SET_ORDER_ACTION = 'PROJECT_SET_ORDER_ACTION'
export const PROJECT_SET_LIMIT_ACTION = 'PROJECT_SET_LIMIT_ACTION'
export const PROJECT_SET_OFFSET_ACTION = 'PROJECT_SET_OFFSET_ACTION'
export const PROJECT_SET_SEARCH_ACTION = 'PROJECT_SET_SEARCH_ACTION'
export const PROJECT_SET_TOTAL_COUNT_ACTION = 'PROJECT_SET_TOTAL_COUNT_ACTION'
export const PROJECT_TOGGLE_SHOW_FAVORITES = 'PROJECT_TOGGLE_SHOW_FAVORITES'
export const PROJECT_TOGGLE_SHOW_ACTIVE = 'PROJECT_TOGGLE_SHOW_ACTIVE'
export const PROJECT_TOGGLE_SHOW_RECENT = 'PROJECT_TOGGLE_SHOW_RECENT'

const MAX_PREFETCH_ITEMS = 200
const projectPageCache = new Map()
const projectPageRequestsInFlight = new Map()
let latestSuggestionRequest = 0
let suggestedProject = null

function invalidateProjectPageCache () {
  projectPageCache.clear()
  projectPageRequestsInFlight.clear()
}

export function projectUpdateAction (project) {
  return {
    type: PROJECT_UPDATE_ACTION,
    project
  }
}

export function projectDeleteAction (project) {
  return {
    type: PROJECT_DELETE_ACTION,
    project
  }
}

function pageItemsAction (pageItems, totalCount) {
  const selected = store.getState().selected.current
  if (suggestedProject && selected === suggestedProject && !pageItems.some(item => item.id === selected.id)) {
    pageItems = [selected, ...pageItems]
  }
  return {
    type: PROJECT_GET_PAGE_ITEMS_ACTION,
    pageItems,
    totalCount
  }
}

export function getPageItems (id) {
  const state = store.getState()
  const {
    search,
    limit,
    offset,
    orderBy,
    order,
    showFavorites,
    showActive,
    showRecent
  } = state.project

  const sort = orderBy ? `${orderBy}.${order}` : null
  const params = {
    query: [],
    limit,
    offset,
    sort,
    recent: showRecent
  }

  if (typeof search === 'string' && search.length > 0) {
    const split = search.split(',').map(s => s.trim())
    if (split.length === 1) {
      if (split[0].length > 0) params.query.push({ key: 'name', value: split[0], op: '~' })
    } else if (split.length === 2) {
      if (split[0].length > 0) params.query.push({ key: 'name', value: split[0], op: '~' })
      if (split[1].length > 0) params.query.push({ key: 'collection_name', value: split[1], op: '~' })
    }
  }

  const hasSearch = typeof search === 'string' && search.trim().length > 0

  if (showFavorites && !hasSearch) {
    params.query.push({ key: 'is_favorite', value: showFavorites, op: '=' })
  }
  applyActiveFilter(params.query, showActive)
  return function (dispatch) {
    const limitNumber = Math.max(1, Number(limit) || 1)
    const offsetNumber = Math.max(0, Number(offset) || 0)
    const cacheKey = getProjectCacheKey({ query: params.query, recent: params.recent, sort: params.sort, limit: limitNumber })
    const cachedPage = getProjectCachedPage(cacheKey, offsetNumber)
    const cachedTotal = getProjectCachedTotal(cacheKey)

    if (cachedPage) {
      dispatch(pageItemsAction(cachedPage, Math.max(Number(cachedTotal) || 0, offsetNumber + cachedPage.length)))
      if (id) {
        getPageItem(id)(dispatch)
      }
      setTimeout(() => {
        prefetchProjectPagesAhead(cacheKey, {
          query: params.query,
          recent: params.recent,
          sort: params.sort,
          limit: limitNumber
        }, offsetNumber)
      }, 0)
      return
    }

    dispatch(busy())
    return fetchProjectsPageWithCache({
      query: params.query,
      recent: params.recent,
      sort: params.sort,
      limit: limitNumber,
      offset: offsetNumber
    }, cacheKey).then(({ pageItems, totalCount }) => {
      const initialTotal = Math.max(Number(totalCount) || 0, offsetNumber + pageItems.length)

      dispatch(notBusy())
      dispatch(pageItemsAction(pageItems, initialTotal))
      if (id) {
        getPageItem(id)(dispatch)
      }

      setTimeout(() => {
        prefetchProjectPagesAhead(cacheKey, {
          query: params.query,
          recent: params.recent,
          sort: params.sort,
          limit: limitNumber
        }, offsetNumber)

        estimateTotalWithLookAhead(
          (nextParams) => fetchProjectsPageWithCache(nextParams, cacheKey),
          { query: params.query, recent: params.recent },
          {
            batchSize: limitNumber,
            firstPageItems: pageItems,
            currentOffset: offsetNumber,
            maxPrefetchItems: MAX_PREFETCH_ITEMS,
            maxPagesAhead: 3
          }
        ).then((count) => {
          const safeCount = Math.max(Number(count) || 0, pageItems.length)
          const nextTotal = Math.max(getProjectCachedTotal(cacheKey), safeCount)
          setProjectCachedTotal(cacheKey, nextTotal)
          dispatch({ type: PROJECT_SET_TOTAL_COUNT_ACTION, totalCount: nextTotal })
        }).catch(() => {})
      }, 0)
    }).catch(handleGetErrors('project', dispatch))
  }
}

function getProjectCacheKey ({ query, recent, sort, limit }) {
  return JSON.stringify({
    query: Array.isArray(query) ? query : [],
    recent: !!recent,
    sort: sort || '',
    limit: Math.max(1, Number(limit) || 1)
  })
}

function getProjectCacheBucket (cacheKey) {
  if (!projectPageCache.has(cacheKey)) {
    projectPageCache.set(cacheKey, { pages: new Map(), totalCount: 0 })
  }
  return projectPageCache.get(cacheKey)
}

function getProjectCachedPage (cacheKey, offset) {
  const bucket = projectPageCache.get(cacheKey)
  return bucket?.pages?.get(offset) || null
}

function setProjectCachedPage (cacheKey, offset, pageItems) {
  const bucket = getProjectCacheBucket(cacheKey)
  bucket.pages.set(offset, Array.isArray(pageItems) ? pageItems : [])
}

function getProjectCachedTotal (cacheKey) {
  const bucket = projectPageCache.get(cacheKey)
  return Number(bucket?.totalCount) || 0
}

function setProjectCachedTotal (cacheKey, totalCount) {
  const bucket = getProjectCacheBucket(cacheKey)
  bucket.totalCount = Math.max(Number(totalCount) || 0, 0)
}

function fetchProjectsPageWithCache (params, cacheKey) {
  const limit = Math.max(1, Number(params?.limit) || 1)
  const offset = Math.max(0, Number(params?.offset) || 0)
  const requestKey = `${cacheKey}|${offset}|${limit}`
  const existing = projectPageRequestsInFlight.get(requestKey)
  if (existing) {
    return existing
  }

  const request = api.getProjects({ ...params, limit, offset }).then((result) => {
    const pageItems = Array.isArray(result?.data?.value) ? result.data.value : []
    const totalCount = Math.max(Number(result?.data?.total_count) || 0, offset + pageItems.length)
    setProjectCachedPage(cacheKey, offset, pageItems)
    setProjectCachedTotal(cacheKey, totalCount)
    return { pageItems, totalCount }
  }).finally(() => {
    projectPageRequestsInFlight.delete(requestKey)
  })

  projectPageRequestsInFlight.set(requestKey, request)
  return request
}

function prefetchProjectPagesAhead (cacheKey, baseParams, currentOffset) {
  const limit = Math.max(1, Number(baseParams?.limit) || 1)
  const pagesAhead = getPrefetchPagesAhead(limit, MAX_PREFETCH_ITEMS, 2)

  if (pagesAhead <= 0) {
    return
  }

  for (let i = 1; i <= pagesAhead; i++) {
    const nextOffset = currentOffset + (i * limit)
    if (getProjectCachedPage(cacheKey, nextOffset)) {
      continue
    }

    fetchProjectsPageWithCache({ ...baseParams, offset: nextOffset }, cacheKey).catch(() => {})
  }
}

function getPageItem (id) {
  const state = store.getState()
  const { sharePointSaveEnabled } = state.login.userData

  const params = {
    query: [{ key: 'id', value: id, op: '=' }],
    limit: 1
  }

  return function (dispatch) {
    dispatch(busy())
    api.getProjects(params).then(result => {
      dispatch(notBusy())
      if (Array.isArray(result.data.value) && result.data.value.length === 1) {
        dispatch({
          type: PROJECT_GET_PAGE_ITEM_ACTION,
          pageItem: result.data.value[0],
          totalCount: result.data.total_count
        })

        dispatch(setSelectedItem(result.data.value[0]))
        if (sharePointSaveEnabled) {
          dispatch(redux.actions.files.getRootFolder(result.data.value[0]))
        }
      }
    }).catch(handleGetErrors('project', dispatch))
  }
}

export function getSuggestionItems ({ preserveManualSelection = false } = {}) {
  return async function (dispatch) {
    const requestId = ++latestSuggestionRequest
    const item = Office.context.mailbox.item
    const state = store.getState()
    if (preserveManualSelection && state.selected.current && state.selected.current !== suggestedProject) {
      return
    }
    if (suggestedProject && state.selected.current === suggestedProject) {
      dispatch(resetSelectedItem())
    }
    suggestedProject = null
    if (!item || (!isOutlookReadMode() && !isOutlookComposeMode())) return

    const selection = store.getState().selected.current
    const isCurrent = () => requestId === latestSuggestionRequest &&
      Office.context.mailbox.item === item &&
      store.getState().page === 'project' &&
      store.getState().selected.current === selection
    const findProject = async params => {
      if (!isCurrent()) return null
      const result = await api.getProjects({ ...params, limit: 1 })
      return result?.data?.value?.[0] || null
    }

    dispatch(busy())
    try {
      const metadata = isOutlookReadMode() ? getEmailMetadataReadMode() : null
      const subject = metadata ? metadata.subject : await getEmailSubjectComposeMode()
      const match = String(subject || '').match(/(?:^|\b)pds-p(\d+)\b/i)
      let project = match
        ? await findProject({ query: [{ key: 'id', value: match[1], op: '=' }] })
        : null

      if (!project && isCurrent()) {
        const address = metadata ? metadata.sender : await getEmailAddressComposeMode()
        const email = String(address || '').trim().toLowerCase()
        if (email) {
          const query = [{ key: 'person_role.person_email', value: email, op: '=' }]
          project = await findProject({ query, recent: true })
          if (!project) project = await findProject({ query, sort: 'updated_at.desc' })
        }
      }

      if (project && isCurrent()) {
        suggestedProject = project
        dispatch({
          type: PROJECT_GET_PAGE_ITEM_ACTION,
          pageItem: project
        })
        dispatch(setSelectedItem(project))
        if (state.login.userData.sharePointSaveEnabled) {
          dispatch(redux.actions.files.getRootFolder(project))
        }
      }
      dispatch(notBusy())
    } catch (error) {
      if (isCurrent()) {
        handleGetErrors('project', dispatch)(error)
      } else {
        dispatch(notBusy())
      }
    }
  }
}

export function toggleFavorite (project) {
  return function (dispatch) {
    const favorite = !project.is_favorite
    const current = store.getState().project.pageItems.find((item) => item.id === project.id) || project
    const updated = { ...current, is_favorite: favorite }
    dispatch(busy())
    api.setProjectFavorite(project.id, favorite).then(() => {
      invalidateProjectPageCache()
      dispatch(projectUpdateAction(updated))
      dispatch(notBusy())
    }).catch(handleUpdateErrors('project', dispatch))
  }
}

/**
 * Sort order
 */
export function setOrder (orderBy, order) {
  return {
    type: PROJECT_SET_ORDER_ACTION,
    orderBy,
    order
  }
}

/**
 * Set limit for pagination
 */
export function setLimit (limit) {
  return {
    type: PROJECT_SET_LIMIT_ACTION,
    limit
  }
}

/**
 * Set offset for pagination
 */
export function setOffset (offset) {
  return {
    type: PROJECT_SET_OFFSET_ACTION,
    offset
  }
}

/**
 * Set search
 */
export function setSearch (search) {
  return {
    type: PROJECT_SET_SEARCH_ACTION,
    search
  }
}

/**
 * Toggle show favorites
 */
export function toggleShowFavorites () {
  return {
    type: PROJECT_TOGGLE_SHOW_FAVORITES
  }
}

/**
 * Toggle show active only
 */
export function toggleShowActive () {
  return {
    type: PROJECT_TOGGLE_SHOW_ACTIVE
  }
}

/**
 * Toggle show recent
 */
export function toggleShowRecent () {
  return {
    type: PROJECT_TOGGLE_SHOW_RECENT
  }
}

function applyActiveFilter (query, showActive) {
  if (!showActive) {
    return
  }

  query.push({ key: 'ended_at', value: null, op: '=' })
}
